import React, { useState, useEffect } from 'react';
import MainLayout from "../components/layout/MainLayout";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import {
  Card,
  Table,
  TableContainer,
  TablePagination,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TextField,
  Button,
  Container,
  Stack,
  Typography,
  Paper,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import CircularProgress from '@mui/material/CircularProgress';
// API
import { fetchCodeAsync, createCodeAsync, takeCodeAsync } from '../redux/reducer/activation/codeSlice';

const Activation = () => {
  const { appId } = useParams();
  const dispatch = useDispatch();

  const [count, setCount] = useState(1);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copiedCode, setCopiedCode] = useState('');

  const codes = useSelector((state) => state.code.list);
  const loading = useSelector((state) => state.code.loading);

  useEffect(() => {
    dispatch(fetchCodeAsync(appId));
  }, [dispatch, appId]);

  const rowsPerPage = 10;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page on search
  };

  // Filter codes by email or code
  const filteredCodes = codes.filter((item) => {
    const email = item.email || '';
    const code = item.code || '';
    const searchLower = search.toLowerCase();
    return email.toLowerCase().includes(searchLower) || code.toLowerCase().includes(searchLower);
  });

  // Sort: Not Activated first, then by creation date
  const sortedCodes = filteredCodes.slice().sort((a, b) => {
    if (a.activated === b.activated) return 0;
    return a.activated ? 1 : -1;
  });

  const handleOpenGenerateDialog = () => {
    setOpenGenerateDialog(true);
  };

  const handleCloseGenerateDialog = () => {
    setOpenGenerateDialog(false);
  };

  const handleGenerateCodes = () => {
    if (count < 1 || count > 100) {
      setSnackbar({ open: true, message: 'Please enter a number between 1 and 100', severity: 'error' });
      return;
    }

    try {
      const codeCount = { count };
      dispatch(createCodeAsync(appId, codeCount)).then(() => {
        dispatch(fetchCodeAsync(appId));
        setSnackbar({ open: true, message: `Successfully generated ${count} activation code(s)!`, severity: 'success' });
        setCount(1);
        handleCloseGenerateDialog();
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate codes. Please try again.', severity: 'error' });
      console.error(error);
    }
  };

  const onTakeCode = (code) => {
    try {
      const codeData = { code };
      dispatch(takeCodeAsync(appId, codeData)).then(() => {
        dispatch(fetchCodeAsync(appId));
        setSnackbar({ open: true, message: `Code ${code} marked as taken!`, severity: 'success' });
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to take code. Please try again.', severity: 'error' });
      console.error(error);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setSnackbar({ open: true, message: 'Code copied to clipboard!', severity: 'success' });
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Statistics
  const totalCodes = codes.length;
  const activatedCodes = codes.filter(c => c.activated).length;
  const takenCodes = codes.filter(c => c.taken).length;
  const availableCodes = codes.filter(c => !c.activated && !c.taken).length;

  return (
    <MainLayout>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="700"
                gutterBottom
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Activation Codes
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Generate and manage activation codes for your application
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenGenerateDialog}
              sx={{
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              Generate Codes
            </Button>
          </Stack>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Codes
                  </Typography>
                  <Typography variant="h3" fontWeight="700">
                    {totalCodes}
                  </Typography>
                </Box>
                <VpnKeyIcon sx={{ fontSize: 50, opacity: 0.3 }} />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Available
                  </Typography>
                  <Typography variant="h3" fontWeight="700">
                    {availableCodes}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Taken
                  </Typography>
                  <Typography variant="h3" fontWeight="700">
                    {takenCodes}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 50, opacity: 0.3 }} />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Activated
                  </Typography>
                  <Typography variant="h3" fontWeight="700">
                    {activatedCodes}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <Card sx={{ mb: 3, p: 2, borderRadius: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by activation code or user email..."
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Card>

        {/* Codes Table */}
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700 }}>S/N</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Activation Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>User Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading activation codes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <VpnKeyIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        {search ? 'No codes found matching your search' : 'No activation codes yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {!search && 'Click "Generate Codes" to create new activation codes'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage > 0
                    ? sortedCodes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : sortedCodes
                  ).map((code, index) => (
                    <TableRow
                      key={code._id}
                      sx={{
                        '&:hover': { bgcolor: 'grey.50' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              bgcolor: 'grey.100',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontWeight: 600,
                            }}
                          >
                            {code.code}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => handleCopyCode(code.code)}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              color: copiedCode === code.code ? 'success.main' : 'primary.main',
                            }}
                          >
                            {copiedCode === code.code ? 'Copied!' : 'Copy'}
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={code.activated ? 'Activated' : 'Not Activated'}
                            size="small"
                            icon={code.activated ? <CheckCircleIcon /> : <CancelIcon />}
                            color={code.activated ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                          {code.taken && !code.activated && (
                            <Chip
                              label="Taken"
                              size="small"
                              color="info"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {code.email ? (
                          <Typography variant="body2">{code.email}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!code.taken && !code.activated && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onTakeCode(code.code)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Mark as Taken
                          </Button>
                        )}
                        {code.taken && !code.activated && (
                          <Chip label="Pending Activation" size="small" color="warning" />
                        )}
                        {code.activated && (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedCodes.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={filteredCodes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </Card>

        {/* Generate Codes Dialog */}
        <Dialog
          open={openGenerateDialog}
          onClose={handleCloseGenerateDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VpnKeyIcon color="primary" />
              <Typography variant="h6" fontWeight="700">
                Generate Activation Codes
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Specify how many activation codes you want to generate. Each code can be used once to activate the application.
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Number of Codes"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              helperText="Enter a number between 1 and 100"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseGenerateDialog}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateCodes}
              variant="contained"
              disabled={count < 1 || count > 100}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              Generate {count} Code{count > 1 ? 's' : ''}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </MainLayout>
  );
};

export default Activation;
